import { ref, watch } from 'vue'
import { defineStore } from 'pinia'

export type BackendAvailabilityIssue = {
  status?: number
  message?: string
  occurredAt: number
}

export const useUIStore = defineStore('ui', () => {
  const isOffcanvasOpen = ref(false)
  const isAlertDialogOpen = ref(true)
  const isBackendUnavailable = ref(false)
  const isBackendUnavailableAlertDismissed = ref(false)
  const backendAvailabilityIssue = ref<BackendAvailabilityIssue | null>(null)

  const OFFCANVAS_SCROLL_LOCK_CLASS = 'has-open-offcanvas'

  function setDocumentClass(className: string, enabled: boolean) {
    if (typeof document === 'undefined') return

    document.documentElement.classList.toggle(className, enabled)
    document.body.classList.toggle(className, enabled)
  }

  /**
   * Set state explicitly (true/false), or toggle if no state is provided.
   */
  function setOffcanvasOpen(state?: boolean) {
    const next = typeof state === 'boolean' ? state : !isOffcanvasOpen.value
    isOffcanvasOpen.value = next
  }

  function toggleOffcanvas() {
    setOffcanvasOpen()
  }

  function openOffcanvas() {
    setOffcanvasOpen(true)
  }

  function closeOffcanvas() {
    setOffcanvasOpen(false)
  }

  /**
   * Alert dialog
   */
  function toggleAlertDialog() {
    isAlertDialogOpen.value = !isAlertDialogOpen.value
  }

  function openAlertDialog() {
    isAlertDialogOpen.value = true
  }

  function closeAlertDialog() {
    isAlertDialogOpen.value = false
  }

  function reportBackendUnavailable(issue: Omit<BackendAvailabilityIssue, 'occurredAt'> = {}) {
    isBackendUnavailable.value = true
    isBackendUnavailableAlertDismissed.value = false
    backendAvailabilityIssue.value = {
      ...issue,
      occurredAt: Date.now(),
    }
  }

  function clearBackendUnavailable() {
    isBackendUnavailable.value = false
    isBackendUnavailableAlertDismissed.value = false
    backendAvailabilityIssue.value = null
  }

  function dismissBackendUnavailableAlert() {
    isBackendUnavailableAlertDismissed.value = true
  }

  // Sync the document class automatically when state changes
  watch(
    isOffcanvasOpen,
    (open) => setDocumentClass(OFFCANVAS_SCROLL_LOCK_CLASS, open),
    { immediate: true }
  )

  return {
    // state
    isOffcanvasOpen,
    isAlertDialogOpen,
    isBackendUnavailable,
    isBackendUnavailableAlertDismissed,
    backendAvailabilityIssue,

    // offcanvas actions
    setOffcanvasOpen,
    toggleOffcanvas,
    openOffcanvas,
    closeOffcanvas,

    // alert actions
    toggleAlertDialog,
    openAlertDialog,
    closeAlertDialog,
    reportBackendUnavailable,
    clearBackendUnavailable,
    dismissBackendUnavailableAlert,
  }
})

export default useUIStore
